import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Camera, ImageIcon, UserPen } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CustomAvatar } from "@/components/common/CustomAvatar";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SettingsModalLayout } from "@/features/user-setting/components/SettingsModalLayout";
import {
  USER_FIRSTNAME_MAX_LENGTH,
  USER_LASTNAME_MAX_LENGTH,
} from "@/constants";
import { useImageUpload } from "@/hooks/useImageUpload";
import { toast } from "@/hooks/useToast";
import { useUser } from "@/hooks/useUser";
import type { ProfileUpdateRequest } from "@/lib/humhub-api/types";

interface ProfileEditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const getProfileFormSchema = (
  t: (key: string, options?: Record<string, unknown>) => string,
) =>
  z.object({
    firstName: z
      .string()
      .max(
        USER_FIRSTNAME_MAX_LENGTH,
        t("common.firstNameTooLong", { maxLength: USER_FIRSTNAME_MAX_LENGTH }),
      ),
    lastName: z
      .string()
      .max(
        USER_LASTNAME_MAX_LENGTH,
        t("common.lastNameTooLong", { maxLength: USER_LASTNAME_MAX_LENGTH }),
      ),
  });

export const ProfileEditModal: React.FC<ProfileEditModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { currentUser, updateUserProfile, isUpdating, refreshUser } = useUser();
  const {
    uploadProfileImage,
    isUploadingProfile,
    profileError,
    resetProfileError,
    uploadBannerImage,
    isUploadingBanner,
    bannerError,
    resetBannerError,
  } = useImageUpload();
  const profileImageInputRef = React.useRef<HTMLInputElement>(null);
  const bannerImageInputRef = React.useRef<HTMLInputElement>(null);
  const wasOpenRef = React.useRef(false);

  const profileFormSchema = React.useMemo(() => getProfileFormSchema(t), [t]);
  type ProfileFormValues = z.infer<typeof profileFormSchema>;

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      firstName: currentUser?.profile?.firstname || "",
      lastName: currentUser?.profile?.lastname || "",
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, errors: formErrors },
  } = form;

  React.useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      resetProfileError();
      resetBannerError();
      reset({
        firstName: currentUser?.profile?.firstname || "",
        lastName: currentUser?.profile?.lastname || "",
      });
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, reset, currentUser, resetProfileError, resetBannerError]);

  if (!isOpen) return null;

  const displayProfileImageUrl = currentUser?.image_url || "";
  const displayBannerImageUrl =
    currentUser?.bannerImageUrl || currentUser?.banner_image_url || "";
  const isUploadingImage = isUploadingProfile || isUploadingBanner;

  const handleProfileImageUpload = () => {
    resetProfileError();
    profileImageInputRef.current?.click();
  };

  const handleBannerImageUpload = () => {
    resetBannerError();
    bannerImageInputRef.current?.click();
  };

  const handleProfileImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadProfileImage(file);
      await refreshUser();
      toast({
        title: t("common.success"),
        description: t(
          "common.profileImageUpdated",
          "Profile image updated successfully",
        ),
      });
    } finally {
      e.target.value = "";
    }
  };

  const handleBannerImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadBannerImage(file);
      await refreshUser();
      toast({
        title: t("common.success"),
        description: t(
          "common.bannerImageUpdated",
          "Background image updated successfully",
        ),
      });
    } finally {
      e.target.value = "";
    }
  };

  const onSubmit = async (data: ProfileFormValues) => {
    try {
      const updateData: ProfileUpdateRequest = {
        profile: {
          ...currentUser?.profile,
          firstname: data.firstName,
          lastname: data.lastName,
        },
      };

      if (isDirty) {
        await updateUserProfile(updateData);
      }

      toast({
        title: t("common.success"),
        description: t("common.profileUpdated"),
      });

      onClose();
    } catch (error) {
      console.error("Failed to update profile:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("common.profileUpdateFailed");
      form.setError("root", { type: "manual", message: errorMessage });
    }
  };

  return (
    <SettingsModalLayout
      isOpen={isOpen}
      onClose={onClose}
      closeDisabled={isUploadingImage}
      icon={UserPen}
      maxWidth="max-w-2xl"
      title={t("common.editProfile")}
    >
      <Form {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <div className="space-y-6">
            <section className="space-y-4">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-gray-900">
                  {t("common.profileImages", "Profile images")}
                </h3>
                <p className="text-sm text-gray-500">
                  {t(
                    "common.profileImagesAutosaveDescription",
                    "Image changes are saved as soon as you upload them.",
                  )}
                </p>
              </div>

              <div className="overflow-hidden rounded-lg border border-gray-200">
                <div className="relative h-40 bg-gray-100">
                  {displayBannerImageUrl ? (
                    <div
                      className="size-full bg-cover bg-center"
                      style={{
                        backgroundImage: `url("${displayBannerImageUrl}")`,
                      }}
                    />
                  ) : (
                    <div className="flex size-full items-center justify-center bg-gray-100 text-gray-400">
                      <ImageIcon className="size-8" />
                    </div>
                  )}

                  {isUploadingBanner && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/45 text-white">
                      <div className="flex items-center gap-2 text-sm">
                        <div className="size-4 animate-spin rounded-full border-b-2 border-white" />
                        <span>{t("common.updating")}</span>
                      </div>
                    </div>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="absolute right-3 top-3 bg-white/95"
                    onClick={handleBannerImageUpload}
                    disabled={isUploadingBanner}
                  >
                    <Camera className="size-4" />
                    {t("common.updateBanner", "Update background")}
                  </Button>
                </div>

                <div className="flex items-center gap-4 border-t bg-white p-4">
                  <div className="relative">
                    <CustomAvatar
                      name={currentUser?.display_name || t("common.profile")}
                      imageUrl={displayProfileImageUrl || undefined}
                      size={72}
                      roundedFull
                    />
                    {isUploadingProfile && (
                      <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45 text-white">
                        <div className="size-4 animate-spin rounded-full border-b-2 border-white" />
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {t("common.avatarImage", "Avatar image")}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t(
                        "common.uploadImageCondition",
                        "PNG, JPEG, JPG (max 5MB) are supported.",
                      )}
                    </p>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleProfileImageUpload}
                    disabled={isUploadingProfile}
                  >
                    <Camera className="size-4" />
                    {t("common.updateProfileImage", "Update avatar")}
                  </Button>
                </div>
              </div>

              {profileError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {t("common.profileImageError", "Profile image error")}:{" "}
                  {profileError}
                </div>
              )}
              {bannerError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {t("common.bannerImageError", "Background image error")}:{" "}
                  {bannerError}
                </div>
              )}
            </section>

            <div className="border-t border-gray-200" />

            <section className="space-y-4">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-gray-900">
                  {t("common.basicInfo", "Basic Info")}
                </h3>
                <p className="text-sm text-gray-500">
                  {t(
                    "common.profileTextSaveDescription",
                    "Name changes are saved when you press Save.",
                  )}
                </p>
              </div>

              {formErrors.root && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                  <AlertCircle className="size-4" />
                  <span className="text-sm">{formErrors.root.message}</span>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("common.firstName")}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t("common.firstNamePlaceholder")}
                          maxLength={USER_FIRSTNAME_MAX_LENGTH}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("common.lastName")}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t("common.lastNamePlaceholder")}
                          maxLength={USER_LASTNAME_MAX_LENGTH}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </section>

            <div className="-mx-6 -mb-6 flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isUploadingImage}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                variant="teal"
                disabled={isUpdating || isUploadingImage || !isDirty}
              >
                {isUpdating ? t("common.saving") : t("common.save")}
              </Button>
            </div>
          </div>
        </form>
      </Form>
      <input
        type="file"
        ref={profileImageInputRef}
        className="hidden"
        accept="image/png,image/jpeg,image/jpg"
        onChange={handleProfileImageChange}
      />
      <input
        type="file"
        ref={bannerImageInputRef}
        className="hidden"
        accept="image/png,image/jpeg,image/jpg"
        onChange={handleBannerImageChange}
      />
    </SettingsModalLayout>
  );
};
